import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import { toast } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";
import InvoiceTaxRows, {
  INVOICE_TAX_HEADERS,
  invoiceTaxTotals,
} from "../../../components/Invoice/InvoiceTaxRows";
import { adjustmentLabel } from "../../../utils/adjustments";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const fmtDateTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const Row = ({ label, children, value }) => (
  <div className="flex items-start">
    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-sm text-gray-900">{children ?? value ?? "—"}</span>
  </div>
);

const Section = ({ title, count, actionBtn, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
      <span className="text-xl font-bold text-gray-900">{title}</span>
      {actionBtn}
    </div>
    {count !== undefined && (
      <p className="text-xs text-gray-500 px-5 pt-3">
        Total <span className="font-semibold">{count}</span>{" "}
        {count === 1 ? "item" : "items"}.
      </p>
    )}
    {children}
  </div>
);

const SectionTable = ({ headers, children }) => (
  <div className="overflow-x-auto px-5 pb-4 pt-2">
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="border-b border-gray-200">
          {headers.map((h) => (
            <th
              key={h}
              className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const VendorInvoiceDetailPage = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.get(`/vendor-invoices/${id}`, { withCredentials: true })
      .then((res) => setInvoice(res.data.data))
      .catch(() => setError("Failed to fetch invoice"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (error)
    return <div className="text-center mt-10 text-danger-600 font-medium">{error}</div>;
  if (!invoice)
    return <div className="text-center mt-10 text-gray-500">Invoice not found.</div>;

  const recv = invoice.flatRateReceivable || invoice.unitBasedReceivable;
  const isUnit = invoice.receivableType === "unit_based";
  const currFull = invoice.currency?.currency;
  const currDisplay = currFull ? `${currFull.code} – ${currFull.name}` : "—";

  const langPair = invoice.job?.languagePair
    ? `${invoice.job.languagePair.sourceLanguage?.name || ""} → ${invoice.job.languagePair.targetLanguage?.name || ""}`
    : "—";

  const pm = invoice.paymentMethod;
  const pmType = pm?.payment_method?.replace(/_/g, " ") || "—";
  let pmDetails = "—";
  if (pm?.bank_transfer_detail) {
    const bt = pm.bank_transfer_detail;
    const parts = [];
    if (bt.bank_name) parts.push(`Bank name: ${bt.bank_name}`);
    if (bt.bank_address) parts.push(`Bank address: ${bt.bank_address}`);
    if (bt.country) parts.push(bt.country);
    pmDetails = parts.join(" ") || "—";
  } else if (pm?.email_payment_detail) {
    pmDetails = pm.email_payment_detail.email || "—";
  }

  const due = Number(invoice.due_amount || 0);
  const paid = Number(invoice.paid_amount || 0);
  const totals = invoiceTaxTotals(invoice);
  const total = totals.total;
  // Nothing is left to collect, so payment coverage no longer applies.
  const fullyWrittenOff =
    Number(invoice.write_off_amount || 0) > 0 && Number(invoice.write_off_amount) >= total;
  let coverage = "Not covered";
  if (total > 0 && due <= 0) coverage = "Fully covered";
  else if (paid > 0) coverage = "Partially covered";
  const coverageColor =
    coverage === "Fully covered"
      ? "text-green-600"
      : coverage === "Partially covered"
      ? "text-amber-600"
      : "text-danger-500";

  const receivableRows = recv
    ? [
        {
          isUnit,
          code: recv.payable_code || "—",
          job: invoice.job ? `LT${invoice.job.id}` : "—",
          jobId: invoice.job?.id,
          jobService: invoice.job?.service?.name || "—",
          langPair,
          unitAmount: recv.unit_amount,
          unit: recv.unit?.name || "—",
          pricePerUnit: recv.price_per_unit,
          subtotal: recv.subtotal,
          extraCharge: adjustmentLabel(recv, "extra_charge"),
          discount: adjustmentLabel(recv, "discount"),
          total: recv.total ?? recv.subtotal,
          filename: recv.file?.file_name || recv.file?.file_code || "—",
          noteForVendor: recv.note_for_vendor || "—",
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      {/* Top two-card row */}
      <div className="flex gap-6 items-start">
        {/* Left card */}
        <div className="w-[63%] bg-white shadow rounded-lg p-8">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <BackButton />
              <h2 className="text-xl font-bold text-gray-900">
                Invoice {invoice.invoice_code}
              </h2>
            </div>
            <button
              onClick={() => toast.error("Download coming soon")}
              className={btn("primary", "sm")}
            >
              Download as PDF
            </button>
          </div>

          <div className="space-y-4 pl-4">
            <Row label="Currency" value={currDisplay} />
            <Row label="Payment method type">
              <span className="capitalize">{pmType}</span>
            </Row>
            <Row label="Payment method details" value={pmDetails} />
            <Row label="Legal entity" value={invoice.vendor?.legal_entity || "—"} />
            <Row label="Issued at" value={fmtDateTime(invoice.issued_at)} />
            <Row label="Created at" value={fmtDateTime(invoice.createdAt)} />
          </div>
        </div>

        {/* Right card (Status) */}
        <div className="flex-1 bg-white shadow rounded-lg p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-8">Status</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Due at:</span>
              <span className="text-sm text-gray-900">{fmtDateTime(invoice.due_at)}</span>
            </div>
            <div className="pt-2">
              <p className="text-sm font-bold text-gray-900 mb-4">Payment coverage</p>
              <div className="space-y-4">
                {!fullyWrittenOff && (
                  <div className="flex items-start">
                    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Coverage:</span>
                    <span className={`text-sm font-medium ${coverageColor}`}>{coverage}</span>
                  </div>
                )}
                {!fullyWrittenOff && (
                  <div className="flex items-start">
                    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Paid amount:</span>
                    <span className="text-sm text-gray-900">{fmtNum(invoice.paid_amount)}</span>
                  </div>
                )}
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Due amount:</span>
                  <span className="text-sm font-bold text-gray-900">{fmtNum(invoice.due_amount)}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Subtotal:</span>
                  <span className="text-sm text-gray-900">{fmtNum(totals.subtotal)}</span>
                </div>
                {invoice.extra_charge_type && (
                  <div className="flex items-start">
                    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Extra charge:</span>
                    <span className="text-sm text-gray-900">{fmtNum(totals.extraCharge)}</span>
                  </div>
                )}
                {invoice.discount_type && (
                  <div className="flex items-start">
                    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Discount:</span>
                    <span className="text-sm text-gray-900">{fmtNum(totals.discount)}</span>
                  </div>
                )}
                {invoice.deduction_type && (
                  <div className="flex items-start">
                    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Deduction:</span>
                    <span className="text-sm text-gray-900">{fmtNum(totals.deduction)}</span>
                  </div>
                )}
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Taxes:</span>
                  <span className="text-sm text-gray-900">{fmtNum(totals.taxTotal)}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">Total:</span>
                  <span className="text-sm text-gray-900">{fmtNum(totals.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Taxes */}
      <Section title="Taxes" count={invoice.taxes?.length ?? 0}>
        <SectionTable headers={INVOICE_TAX_HEADERS}>
          <InvoiceTaxRows taxes={invoice.taxes} />
        </SectionTable>
      </Section>

      {/* Receivables */}
      <Section title="Receivables" count={receivableRows.length}>
        <div className="overflow-x-auto px-5 pb-4 pt-2">
          <table className="w-full text-xs border-collapse min-w-[1000px]">
            <thead>
              <tr className="border-b border-gray-200">
                {[
                  "Code", "Job", "Service", "Language pair",
                  "Unit amount", "Unit", "Price per unit", "Subtotal",
                  "Extra charge", "Discount", "Total", "Filename",
                  "Note for vendor", "",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {receivableRows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="py-3 text-xs text-gray-400">
                    No results found.
                  </td>
                </tr>
              ) : (
                receivableRows.map((r, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      <span className="text-teal-600 font-medium">{r.code}</span>
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
                      {r.jobId ? (
                        <Link
                          to={`/jobs/${r.jobId}`}
                          className="text-brand-600 hover:underline"
                        >
                          {r.job}
                        </Link>
                      ) : (
                        r.job
                      )}
                    </td>
                    <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                      {r.jobService}
                    </td>
                    <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                      {r.langPair}
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-700">
                      {r.isUnit ? r.unitAmount : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-2 pr-4 text-gray-700">
                      {r.isUnit ? r.unit : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-700">
                      {r.isUnit ? fmtNum(r.pricePerUnit) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-2 pr-4 font-semibold text-gray-900 text-right whitespace-nowrap">
                      {fmtNum(r.subtotal)}
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-700">{r.extraCharge}</td>
                    <td className="py-2 pr-4 text-right text-gray-700">{r.discount}</td>
                    <td className="py-2 pr-4 font-semibold text-gray-900 text-right whitespace-nowrap">
                      {fmtNum(r.total)}
                    </td>
                    <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                      {r.filename}
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{r.noteForVendor}</td>
                    <td className="py-2 whitespace-nowrap">
                      <button
                        onClick={() => toast.error("Remove not yet implemented")}
                        className={btn("warning", "sm")}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Linked payments */}
      <Section title="Linked Payments">
        <SectionTable
          headers={[
            "Code", "Linked invoices", "Invoice coverage",
            "Payment method type", "Paid at", "Amount",
            "Linked amount", "Unlinked amount",
            "Amount covered in this invoice", "Note",
          ]}
        >
          <tr>
            <td colSpan={10} className="py-3 text-xs text-gray-400">
              No results found.
            </td>
          </tr>
        </SectionTable>
      </Section>

      {/* Related files */}
      <Section title="Related Files">
        <SectionTable
          headers={["File #", "Filename", "Size", "Uploaded at", "Uploaded by"]}
        >
          <tr>
            <td colSpan={5} className="py-3 text-xs text-gray-400">
              No results found.
            </td>
          </tr>
        </SectionTable>
      </Section>

      {/* Messages */}
      <Section title="Messages">
        <div className="px-5 py-4 space-y-3">
          <textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write a message..."
            className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <button
            disabled={!message.trim()}
            className={btn("primary", "sm")}
          >
            Send
          </button>
        </div>
      </Section>
    </div>
  );
};

export default VendorInvoiceDetailPage;
